import {
  createAbsenceReasonRequest,
  deactivateAbsenceReasonRequest,
  getAbsenceReasonRequest,
  parseAbsenceReasonError,
  updateAbsenceReasonRequest,
} from 'api/requests/absence-reason-requests';
import { useCallback, useRef, useState } from 'react';
import {
  ABSENCE_REASON__CONCURRENT_CHANGE,
  ABSENCE_REASON__DEACTIVATED,
  ABSENCE_REASON__NOT_FOUND,
  ABSENCE_REASON__PANEL_LOAD_ERROR,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import { IAbsenceReason } from 'types/absence-reason/IAbsenceReason';
import {
  AbsenceReasonFormValues,
  getAbsenceReasonErrorKey,
  getServerFieldError,
  ServerFieldError,
  toRequest,
} from '../utils';

export type PanelStatus = 'closed' | 'loading' | 'ready' | 'loadError';
export type PanelMode = 'add' | 'edit';

export interface UseAbsenceReasonPanelCallbacks {
  onSaved: () => void;
  onDeactivated: () => void;
  onClosed: () => void;
  onGone: (messageKey: string) => void;
  onStale: () => void;
  onError: (messageKey: string) => void;
}

/** `null` = the add form; a number = the reason being edited. */
type PanelTarget = number | null;

export type RetryAction = 'save' | 'deactivate';

export const useAbsenceReasonPanel = (callbacks: UseAbsenceReasonPanelCallbacks) => {
  const [mode, setMode] = useState<PanelMode>('add');
  const [status, setStatus] = useState<PanelStatus>('closed');
  const [reason, setReason] = useState<IAbsenceReason | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDeactivate, setIsConfirmingDeactivate] = useState(false);
  const [loadErrorKey, setLoadErrorKey] = useState<string | null>(null);
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null);
  // Which request the error bar's Retry must repeat; a failed deactivation must not be retried as a save.
  const [retryAction, setRetryAction] = useState<RetryAction>('save');
  const [serverFieldError, setServerFieldError] = useState<ServerFieldError | null>(null);
  // Refs, not state: the double-submit guard and the "is this panel still showing that target" check must see
  // the current value right after an await, before React re-renders. `sessionRef` changes on every open/close,
  // so a response for an earlier session (even of the same reason) is recognised and dropped.
  const savingRef = useRef(false);
  const sessionRef = useRef(0);
  const targetRef = useRef<PanelTarget>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const load = useCallback(async (target: PanelTarget) => {
    const session = sessionRef.current;
    setStatus('loading');
    setLoadErrorKey(null);
    try {
      const loaded = target === null ? null : await getAbsenceReasonRequest(target);
      if (session !== sessionRef.current) return;
      setReason(loaded);
      setStatus('ready');
    } catch (error) {
      if (session !== sessionRef.current) return;
      setLoadErrorKey(getAbsenceReasonErrorKey(parseAbsenceReasonError(error), ABSENCE_REASON__PANEL_LOAD_ERROR));
      setStatus('loadError');
    }
  }, []);

  const start = useCallback(
    (nextMode: PanelMode, target: PanelTarget) => {
      sessionRef.current += 1;
      targetRef.current = target;
      setMode(nextMode);
      setReason(null);
      setSaveErrorKey(null);
      setServerFieldError(null);
      setIsConfirmingDeactivate(false);
      load(target);
    },
    [load],
  );

  const openAdd = useCallback(() => start('add', null), [start]);
  const openEdit = useCallback((row: IAbsenceReason) => start('edit', row.id), [start]);
  const retryLoad = useCallback(() => load(targetRef.current), [load]);

  const reset = useCallback(() => {
    sessionRef.current += 1;
    targetRef.current = null;
    setStatus('closed');
    setReason(null);
    setLoadErrorKey(null);
    setSaveErrorKey(null);
    setServerFieldError(null);
    setIsConfirmingDeactivate(false);
  }, []);

  const close = useCallback(() => {
    reset();
    callbacksRef.current.onClosed();
  }, [reset]);

  /**
   * Shared failure handling for save and deactivate. The user can close the panel while a request is in flight;
   * the outcome is then reported as a toast and the panel is left alone (lesson from the US 122 review).
   */
  const handleFailure = useCallback(
    (error: unknown, session: number, action: RetryAction) => {
      const parsed = parseAbsenceReasonError(error);
      const messageKey = getAbsenceReasonErrorKey(parsed, GENERAL__ERROR_MESSAGE);
      const stillShowing = session === sessionRef.current;
      if (parsed.key === ABSENCE_REASON__NOT_FOUND || parsed.key === ABSENCE_REASON__DEACTIVATED) {
        if (stillShowing) reset();
        callbacksRef.current.onGone(messageKey);
        return;
      }
      if (!stillShowing) {
        if (parsed.key === ABSENCE_REASON__CONCURRENT_CHANGE) callbacksRef.current.onStale();
        callbacksRef.current.onError(messageKey);
        return;
      }
      const fieldError = getServerFieldError(parsed);
      if (fieldError) {
        setServerFieldError(fieldError);
      } else if (parsed.key === ABSENCE_REASON__CONCURRENT_CHANGE) {
        // Reload the reason so the next Save carries the fresh rowVersion, not the stale one.
        setSaveErrorKey(ABSENCE_REASON__CONCURRENT_CHANGE);
        callbacksRef.current.onStale();
        load(targetRef.current);
      } else {
        // Network errors, 5xx, timeouts: keep the panel and the user's input, and offer a retry.
        setRetryAction(action);
        setSaveErrorKey(messageKey);
      }
    },
    [reset, load],
  );

  const save = useCallback(
    async (values: AbsenceReasonFormValues) => {
      if (savingRef.current || status !== 'ready') return;
      const target = targetRef.current;
      if (mode === 'edit' && (target === null || !reason)) return;
      const session = sessionRef.current;
      savingRef.current = true;
      setIsSaving(true);
      setSaveErrorKey(null);
      setServerFieldError(null);
      try {
        const body = toRequest(values);
        if (mode === 'add') await createAbsenceReasonRequest(body);
        else await updateAbsenceReasonRequest(target as number, body, (reason as IAbsenceReason).rowVersion);
        if (session === sessionRef.current) reset();
        callbacksRef.current.onSaved();
      } catch (error) {
        handleFailure(error, session, 'save');
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [mode, reason, status, reset, handleFailure],
  );

  const requestDeactivate = useCallback(() => {
    if (mode === 'edit' && status === 'ready' && reason?.isActive) setIsConfirmingDeactivate(true);
  }, [mode, status, reason]);

  const cancelDeactivate = useCallback(() => {
    if (!savingRef.current) setIsConfirmingDeactivate(false);
  }, []);

  const confirmDeactivate = useCallback(async () => {
    if (savingRef.current || !reason) return;
    const session = sessionRef.current;
    savingRef.current = true;
    setIsSaving(true);
    setSaveErrorKey(null);
    try {
      await deactivateAbsenceReasonRequest(reason.id, reason.rowVersion);
      if (session === sessionRef.current) reset();
      callbacksRef.current.onDeactivated();
    } catch (error) {
      setIsConfirmingDeactivate(false);
      handleFailure(error, session, 'deactivate');
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [reason, reset, handleFailure]);

  return {
    mode,
    status,
    reason,
    isSaving,
    isConfirmingDeactivate,
    loadErrorKey,
    saveErrorKey,
    retryAction,
    serverFieldError,
    openAdd,
    openEdit,
    retryLoad,
    close,
    save,
    requestDeactivate,
    cancelDeactivate,
    confirmDeactivate,
  };
};

export type UseAbsenceReasonPanelResult = ReturnType<typeof useAbsenceReasonPanel>;
